import React from 'react';
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SHARK_SPECIES, SharkConfig, CostumeConfig } from '../../../components/SharkAvatarSelector';

interface ChooseSharkCardProps {
  currentIndex: number;
  currentShark: SharkConfig;
  currentCostumeObj: CostumeConfig;
  direction: number;
  onNavigate: (dir: number) => void;
  onSelectSpecies: (speciesId: string, isNext: boolean) => void;
}

export function ChooseSharkCard({
  currentIndex,
  currentShark,
  currentCostumeObj,
  direction,
  onNavigate,
  onSelectSpecies
}: ChooseSharkCardProps) {
  return (
    <div style={{
      border: '1px solid var(--admin-card-border)',
      borderRadius: '0',
      backgroundColor: 'var(--admin-card-bg)',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      flex: 1
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--admin-card-subtext)', textTransform: 'uppercase' }}>
          CHOOSE YOUR SHARK
        </span>
        <span style={{ fontSize: '11px', color: 'var(--admin-card-subtext)' }}>
          {currentIndex + 1} of {SHARK_SPECIES.length}
        </span>
      </div>

      {/* Shark Display Frame Box - HEIGHT 210px */}
      <div 
        className="shark-frame-box"
        style={{
          position: 'relative',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backgroundColor: 'rgba(255, 255, 255, 0.01)',
          borderRadius: '0',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '210px',
          overflow: 'hidden',
          flex: 1
        }}
      >
        {/* Dim Blueish Ambient Radial Glow */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at center, rgba(14, 165, 233, 0.08) 0%, rgba(3, 19, 64, 0.04) 60%, transparent 80%)',
          pointerEvents: 'none'
        }} />

        {/* Left Navigation Arrow */}
        <button
          type="button"
          onClick={() => onNavigate(-1)}
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10
          }}
        >
          <ChevronLeft size={14} />
        </button>

        {/* Right Navigation Arrow */}
        <button
          type="button"
          onClick={() => onNavigate(1)}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10
          }}
        >
          <ChevronRight size={14} />
        </button>

        {/* Animated Side-view Graphic */}
        <div className="shark-img-wrapper" style={{ height: '115px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '6px', zIndex: 2 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentShark.id}-${currentCostumeObj.id}`}
              initial={{ opacity: 0, x: direction > 0 ? 50 : -50, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: direction > 0 ? -50 : 50, scale: 0.9 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
            >
              <motion.img
                src={currentCostumeObj.sideView}
                alt={currentShark.species}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                style={{ height: '100%', maxWidth: '230px', objectFit: 'contain', filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.3))' }}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Cyan Species Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 10px',
          borderRadius: '100px',
          backgroundColor: 'rgba(62, 143, 166, 0.15)',
          border: '1px solid rgba(62, 143, 166, 0.3)',
          color: '#38bdf8',
          fontSize: '11px',
          fontWeight: 700,
          marginBottom: '2px',
          zIndex: 2
        }}>
          <Sparkles size={11} /> {currentShark.species}
        </div>

        {/* Personality Subtext */}
        <span style={{ fontSize: '10px', fontStyle: 'italic', color: 'var(--admin-card-subtext)', marginBottom: '6px', zIndex: 2 }}>
          {currentShark.personality}
        </span>

        {/* Pagination Dots */}
        <div style={{ display: 'flex', gap: '4px', zIndex: 2 }}>
          {SHARK_SPECIES.map((s, idx) => (
            <div
              key={s.id}
              onClick={() => onSelectSpecies(s.id, idx > currentIndex)}
              style={{
                width: idx === currentIndex ? '16px' : '6px',
                height: '4px',
                borderRadius: '2px',
                backgroundColor: idx === currentIndex ? '#38bdf8' : 'rgba(255, 255, 255, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
